import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Course } from '../models/course';
import { iLessonMeta } from '../interfaces/iLessonMeta';
import { Review } from '../models/review';
import { Document } from '../models/document';
import { iHttpPaginatedResponse } from '../interfaces/iHttpPaginatedResponse';
import { Quiz } from '../models/quiz';
import { UserQuestionAnswer } from '../models/userQuestionAnswer';
import { RoleFilters } from '../models/roleFilters';
import { env } from 'process';

@Injectable({
  providedIn: 'root'
})

export class CourseService {

    constructor(private http: HttpClient) {
    }

    public addToWatchlist(courseId:number){
      return this.http.put(environment.apiEndpoint + '/api/watchlist', {courseId:courseId}).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public removeFromWatchlist(courseId:number){
      return this.http.delete(environment.apiEndpoint + '/api/watchlist/' + courseId).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public getWatchlistStatus(courseId:number){
      return this.http.get(environment.apiEndpoint + '/api/watchlist/' + courseId).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public getuserWatchlist(limit?:number):Observable<Course[]>{
      return this.http.get<Course[]>(environment.apiEndpoint + '/api/userWatchlist/' + limit).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public getCurrentlyWatching(limit:number): Observable<Course[]>{
      return this.http.get<Course[]>(environment.apiEndpoint + '/api/course/watching/' + limit).pipe(
        map(
          res => {
            return res;
          }
        )
      );
    }

    public getRelatedCourses($courseId:number): Observable<any>{
      return this.http.get<any>(environment.apiEndpoint + '/api/course/related/'+$courseId).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public featured(loggedIn:boolean=false): Observable<Course[]>{
      if (loggedIn) {
        return this.http.get<Course[]>(environment.apiEndpoint + `/api/featuredWithMeta`).pipe(
          map(res => {
              return res;
          })
        );
      } else {
        return this.http.get<Course[]>(environment.apiEndpoint + `/api/featured`).pipe(
          map(res => {
              return res;
          })
        );
      }
    }

    public getQuiz(quizId:number): Observable<Quiz>{
        return this.http.get<Quiz>(environment.apiEndpoint + `/api/course/quiz/${quizId}`).pipe(
            map(res => {
                return new Quiz(res);
            })
        );
    }

    public getDocuments(course_id):Observable<any>{
      return this.http.get<any>(environment.apiEndpoint + `/api/course/documents/${course_id}`).pipe(
        map(res => {
          let results = [];
          res.forEach(doc=>{
            results.push(new Document(doc));
          })
          res = results;
          return res;
        })
      );
    }

    public uploadDocument(fname:string, ftype:string, fb64:string, courseId:number):Observable<any>{
      return this.http.post(environment.apiEndpoint+'/api/admin/course/'+courseId+'/document', {fname:fname, ftype:ftype, fb64:fb64}).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public deleteDocument(docId:number, courseId:number):Observable<any>{
      return this.http.delete(environment.apiEndpoint+`/api/admin/course/${courseId}/document/${docId}`).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public getRandom(){
        return this.http.get<Course[]>(environment.apiEndpoint + `/api/course/random`).pipe(
            map(res => {
                return res;
            })
        );
    }

    public getUserQuizAnswers(quiz_id): Observable<UserQuestionAnswer[]>{
        return this.http.get<UserQuestionAnswer[]>(environment.apiEndpoint + `/api/course/quiz/answer/${quiz_id}`).pipe(
            map(res => {
                let answers = [];
                res.forEach(answer=>{
                    answers.push(new UserQuestionAnswer(answer));
                })
                return answers;
            })
        );
    }

    public postQuizQuestionAnswer(answer:UserQuestionAnswer, course_id): Observable<UserQuestionAnswer>{
        return this.http.post<UserQuestionAnswer>(environment.apiEndpoint + `/api/course/quiz/answer/${course_id}`, answer).pipe(
            map(res => {
                return new UserQuestionAnswer(res);
            })
        );
    }

    public checkCourseComplete(course_id): Observable<any>{
        return this.http.get<any>(environment.apiEndpoint + `/api/course/complete/${course_id}`).pipe(
            map(res => {
                return res;
            })
        );
    }

    public getCourse(course_id, publishedOnly:boolean=false): Observable<Course> {
        return this.http.get<Course>(environment.apiEndpoint + `/api/course/${course_id}?published=${publishedOnly}`).pipe(
            map(res => {
                return res;
            })
        );
    }

    public getPurchasedCourses(): Observable<Course[]> {
        return this.http.get<any>(environment.apiEndpoint + `/api/course/purchased`).pipe(
            map(res => {
                let courses=[];
                res.forEach(courseLink=>{
                    courses.push(new Course(courseLink.course));
                })
                return courses;
            })
        );
    }

    public getCourseMeta(course_id): Observable<any> {
        return this.http.get<any>(environment.apiEndpoint + `/api/course/usermeta/${course_id}`).pipe(
            map(res => {
                return res;
            })
        );
    }

    public updateLessonMeta(lessonMeta:iLessonMeta): Observable<any>{
        return this.http.post<any>(environment.apiEndpoint + `/api/lesson/usermeta`,lessonMeta).pipe(
            map(res => {
                return res;
            })
        );
    }

    public postReview(review:Review): Observable<Review>{
        return this.http.post<Review>(environment.apiEndpoint + `/api/course/review`,review).pipe(
            map(res => {
                return new Review(res);
            })
        );
    }

    public getReviews(course_id,page): Observable<iHttpPaginatedResponse> {
        return this.http.get<iHttpPaginatedResponse>(environment.apiEndpoint + `/api/course/review/${course_id}?page=${page}`).pipe(
            map(res => {
                let results = [];
                res.data.forEach(review=>{
                    results.push(new Review(review));
                })

                res.data = results;
                return res;
            })
        );
    }

    public getSupportedRandom(): Observable<any> {
        return this.http.get<any>(environment.apiEndpoint + `/api/course/supporter`).pipe(
            map(res => {
                return res;
            })
        );
    }

    public updateCourse(course:Course, supporters:any, tutors:any, categories:any){
      return this.http.put(environment.apiEndpoint + '/api/admin/course', {course:course, supporters:supporters, tutors:tutors, categories:categories}).pipe(
        map(
          res => {
            return res;
          }
        )
      );
    }

    public updateMediaId(id:number, course_id:number):Observable<any>{
      return this.http.put(environment.apiEndpoint+'/api/admin/courseMediaId', {mediaId:id, courseId:course_id}).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public deleteCourse(id:number):Observable<any>{
      return this.http.delete(environment.apiEndpoint+`/api/course/${id}`).pipe(
        map(
          res=>{
            return res;
          }
        )
      );
    }

    public getTutorCourses(tutorId:number, roleFilters:RoleFilters, loggedIn:boolean=false):Observable<any>{
      let userRoles=[];
      if (roleFilters.vet) userRoles.push(1);
      if (roleFilters.vetNurse) userRoles.push(2);
      if (roleFilters.nonClinical) userRoles.push(3);
      if (roleFilters.practiceContent) userRoles.push(4);
      if (loggedIn) {
        return this.http.get(environment.apiEndpoint+'/api/courses/tutorWithMeta/'+tutorId+'?user_roles='+userRoles.join()).pipe(
          map(
            res=>{
              return res;
            }
          )
        );
      } else {
        return this.http.get(environment.apiEndpoint+'/api/courses/tutor/'+tutorId+'?user_roles='+userRoles.join()).pipe(
          map(
            res=>{
              return res;
            }
          )
        );
      }
    }

    public getSupporterCourses(supporterId:number, roleFilters:RoleFilters, loggedIn:boolean=false):Observable<any>{
      let userRoles=[];
      if (roleFilters.vet) userRoles.push(1);
      if (roleFilters.vetNurse) userRoles.push(2);
      if (roleFilters.nonClinical) userRoles.push(3);
      if (roleFilters.practiceContent) userRoles.push(4);
      if (loggedIn) {
        return this.http.get(environment.apiEndpoint+'/api/courses/supporterWithMeta/'+supporterId+'?user_roles='+userRoles.join()).pipe(
          map(
            res=>{
              return res;
            }
          )
        );
      } else {
        return this.http.get(environment.apiEndpoint+'/api/courses/supporter/'+supporterId+'?user_roles='+userRoles.join()).pipe(
          map(
            res=>{
              return res;
            }
          )
        );
      }
    }
}